import * as React from 'react'
import {useState} from 'react'
import {observer} from 'mobx-react'
import IMask from 'imask'
import {IMaskInput} from 'react-imask'
import cn from 'classnames'
import {Transition} from 'react-transition-group'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {ErrorMessage} from '../../../ui/ErrorMessage'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {buildSlideTransitionProps} from '../../../../lib/helpers'
import {InfoToggle} from '../../../ui/InfoToggle'
import {IconInfo} from '../../../ui/Icons'
import {InputCheckboxWrapper} from '../../../wrappers/InputCheckableWrapper'
import {FormPart} from '../../../wrappers/FormPart'
import {InputWrapper} from '../../../wrappers/InputWrapper'
import {TextareaWrapper} from '../../../wrappers/TextareaWrapper'
import {SelectWrapper} from '../../../wrappers/SelectWrapper'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {DirtyWrapper} from '../../../wrappers/DirtyWrapper'

let moment = require('moment')

///////////////////////////////////////////////////////////////////////////////

export const PaymentProfileForm = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const form = api.paymentForm
  const formData = form.data
  const errors = form.validationErrors

  const salutation = formData.stored_customer_salutation || ''
  const firstName = formData.stored_customer_firstname || ''
  const lastName = formData.stored_customer_lastname || ''
  const email = formData.stored_customer_email || ''
  let birthdate = formData.stored_customer_birthdate || ''
  const emailPermission = formData.stored_customer_email_permission
  const message = formData.stored_customer_message || ''
  const donationReceipt = !!formData.stored_customer_donation_receipt

  const showStoredCustomerSalutation = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerSalutation',
  )
  const showStoredCustomerFirstname = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerFirstname',
  )
  const showStoredCustomerLastname = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerLastname',
  )
  const showStoredCustomerEmail = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerEmail',
  )
  const showStoredCustomerBirthdate = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerBirthdate',
  )
  const showStoredCustomerEmailPermission = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerEmailPermission',
  )
  const showStoredCustomerMessage = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerMessage',
  )
  const showStoredCustomerDonationReceipt = api.resolveConfigWithSingleResult<boolean>(
    'showStoredCustomerDonationReceipt',
  )

  const setSalutation = (value: string) =>
    (formData.stored_customer_salutation = value)
  const setFirstName = (value: string) =>
    (formData.stored_customer_firstname = value)
  const setLastName = (value: string) =>
    (formData.stored_customer_lastname = value)
  const setEmail = (value: string) => (formData.stored_customer_email = value)
  const setBirthdate = (value: string) =>
    (formData.stored_customer_birthdate = value)
  const setEmailPermission = (value: boolean) =>
    (formData.stored_customer_email_permission = value)
  const setMessage = (value: string) =>
    (formData.stored_customer_message = value)
  const setDonationReceipt = (value: boolean) =>
    (formData.stored_customer_donation_receipt = value)

  const submit = () => form.validateAndSend()

  const salutations = api.resolveConfig('salutations')
  const {uiTransitionTimeout: timeout} = api.computedConfig
  const slideTransitionProps = buildSlideTransitionProps({timeout})

  const infoToggleTitleBirthdate = api.trans(
    'info_toggle.stored_customer_birthdate.title',
    {defaultValue: null},
  )
  const infoToggleTextHTMLBirthdate = api.trans(
    'info_toggle.stored_customer_birthdate.text_html',
    {defaultValue: null},
  )
  const [
    infoToggleExpandedBirthdate,
    setInfoToggleExpandedBirthdate,
  ] = useState(false)

  const {dateStoreFormat, dateDisplayFormat} = api.computedConfig
  const dateDisplayFormatTranslated = api.trans('date_display_format', {
    defaultValue: dateDisplayFormat,
  })
  const birthdateDate = moment(birthdate, dateStoreFormat, true)

  if (birthdateDate.isValid()) {
    birthdate = birthdateDate.format(dateDisplayFormat)
  }

  const birthdateMaskProps = {
    mask: dateDisplayFormat,
    blocks: {
      YYYY: {
        mask: IMask.MaskedRange,
        from: 1900,
        to: 2100,
        maxLength: 4,
      },
      MM: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
        maxLength: 2,
      },
      DD: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 31,
        maxLength: 2,
      },
    },
    onAccept: (value, mask) => {
      const date = moment(value, dateDisplayFormat, true)
      let v = value

      if (date.isValid()) {
        v = date.format(dateStoreFormat)
      }

      setBirthdate(v)
    },
  }

  return (
    <fieldset>
      <legend>{api.trans('blocks.payment_profile.legend')}</legend>

      <FormPart className="payment-profile-form" onSubmit={submit}>
        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_START} form={form} />

        {showStoredCustomerSalutation &&
          (() => {
            const id = api.ui.generateUniqueElementId(
              'stored_customer_salutation',
            )
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_salutation-error',
            )

            return (
              <div className="form-block stored_customer_salutation">
                <div className="field-block">
                  <label className="label-block" htmlFor={id}>
                    {api.trans('payment_form.stored_customer_salutation')}
                  </label>
                  <InputWrapper form={form} fields="stored_customer_salutation">
                    <DirtyWrapper
                      form={form}
                      fields="stored_customer_salutation"
                    >
                      <SelectWrapper>
                        <select
                          className="input-select"
                          id={id}
                          aria-describedby={errorId}
                          value={salutation}
                          name="stored_customer_salutation"
                          onChange={(e) => setSalutation(e.target.value)}
                          autoComplete="honorific-prefix"
                        >
                          <option value="" disabled={true}>
                            {api.trans(
                              'payment_form.placeholders.stored_customer_salutation',
                            )}
                          </option>

                          {salutations.map((salutation, key) => (
                            <option key={salutation} value={salutation}>
                              {api.trans(
                                `payment_form.salutations.${salutation}`,
                                {defaultValue: salutation},
                              )}
                            </option>
                          ))}
                        </select>
                      </SelectWrapper>
                    </DirtyWrapper>
                  </InputWrapper>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_salutation"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_1} form={form} />

        {showStoredCustomerFirstname &&
          (() => {
            const id = api.ui.generateUniqueElementId(
              'stored_customer_firstname',
            )
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_firstname-error',
            )

            return (
              <div className="form-block stored_customer_firstname">
                <div className="field-block">
                  <label className="label-block" htmlFor={id}>
                    {api.trans('payment_form.stored_customer_firstname')}
                  </label>
                  <InputWrapper form={form} fields="stored_customer_firstname">
                    <DirtyWrapper
                      form={form}
                      fields="stored_customer_firstname"
                    >
                      <input
                        type="text"
                        id={id}
                        aria-describedby={errorId}
                        className="input-text"
                        name="stored_customer_firstname"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        autoCapitalize="words"
                        placeholder={api.trans(
                          'payment_form.placeholders.stored_customer_firstname',
                        )}
                      />
                    </DirtyWrapper>
                  </InputWrapper>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_firstname"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_2} form={form} />

        {showStoredCustomerLastname &&
          (() => {
            const id = api.ui.generateUniqueElementId(
              'stored_customer_lastname',
            )
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_lastname-error',
            )

            return (
              <div className="form-block stored_customer_lastname">
                <div className="field-block">
                  <label className="label-block" htmlFor={id}>
                    {api.trans('payment_form.stored_customer_lastname')}
                  </label>
                  <InputWrapper form={form} fields="stored_customer_lastname">
                    <DirtyWrapper form={form} fields="stored_customer_lastname">
                      <input
                        type="text"
                        id={id}
                        aria-describedby={errorId}
                        className="input-text"
                        name="stored_customer_lastname"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        autoCapitalize="words"
                        placeholder={api.trans(
                          'payment_form.placeholders.stored_customer_lastname',
                        )}
                      />
                    </DirtyWrapper>
                  </InputWrapper>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_lastname"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_3} form={form} />

        {showStoredCustomerEmail &&
          (() => {
            const id = api.ui.generateUniqueElementId('stored_customer_email')
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_email-error',
            )

            return (
              <div className="form-block stored_customer_email">
                <div className="field-block">
                  <label className="label-block" htmlFor={id}>
                    {api.trans('payment_form.stored_customer_email')}
                  </label>
                  <InputWrapper form={form} fields="stored_customer_email">
                    <DirtyWrapper form={form} fields="stored_customer_email">
                      <input
                        type="email"
                        id={id}
                        aria-describedby={errorId}
                        className="input-text"
                        name="stored_customer_email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        placeholder={api.trans(
                          'payment_form.placeholders.stored_customer_email',
                        )}
                      />
                    </DirtyWrapper>
                  </InputWrapper>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_email"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_4} form={form} />

        {showStoredCustomerBirthdate &&
          (() => {
            const id = api.ui.generateUniqueElementId(
              'stored_customer_birthdate',
            )
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_birthdate-error',
            )
            const infoToggleIdKeyBirthdate =
              'info-toggle-stored_customer_birthdate'

            return (
              <div
                className={cn('form-block', 'stored_customer_birthdate', {
                  'has-info-button': infoToggleTextHTMLBirthdate,
                })}
              >
                <div className="field-block">
                  <label className="label-block" htmlFor={id}>
                    {api.trans('payment_form.stored_customer_birthdate')}
                  </label>
                  <InputWrapper form={form} fields="stored_customer_birthdate">
                    <DirtyWrapper
                      form={form}
                      fields="stored_customer_birthdate"
                    >
                      <IMaskInput
                        {...birthdateMaskProps}
                        type="text"
                        inputMode="numeric"
                        data-pattern="[0-9]*"
                        id={id}
                        aria-describedby={errorId}
                        className="input-text"
                        name="stored_customer_birthdate"
                        placeholder={dateDisplayFormatTranslated}
                        value={birthdate}
                        autoComplete="bday"
                      />
                    </DirtyWrapper>
                  </InputWrapper>
                  <div className="icons-block">
                    {infoToggleTextHTMLBirthdate && (
                      <button
                        type="button"
                        aria-label={infoToggleTitleBirthdate}
                        onClick={() =>
                          setInfoToggleExpandedBirthdate((v) => !v)
                        }
                        aria-expanded={infoToggleExpandedBirthdate}
                        aria-controls={api.ui.generateUniqueElementId(
                          infoToggleIdKeyBirthdate,
                        )}
                        className={cn('info-button', {
                          expanded: infoToggleExpandedBirthdate,
                          collapsed: !infoToggleExpandedBirthdate,
                        })}
                      >
                        <span className="icon">
                          <IconInfo />
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_birthdate"
                  id={errorId}
                />

                <InfoToggle
                  idKey={infoToggleIdKeyBirthdate}
                  className={infoToggleIdKeyBirthdate}
                  textHTML={infoToggleTextHTMLBirthdate}
                  expanded={infoToggleExpandedBirthdate}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_5} />

        {showStoredCustomerEmailPermission &&
          (() => {
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_email_permission-error',
            )
            const infoId = api.ui.generateUniqueElementId(
              'stored_customer_email_permission-info',
            )
            const infoHTML = api.trans(
              'blocks.payment_profile.email_permission_info_html',
            )

            return (
              <div className="form-block stored_customer_email_permission">
                <div className="field-block">
                  <RippleWrapper>
                    <label className="label-checkbox-block">
                      <InputCheckboxWrapper
                        form={form}
                        fields="stored_customer_email_permission"
                      >
                        <DirtyWrapper
                          form={form}
                          fields="stored_customer_email_permission"
                        >
                          <input
                            type="checkbox"
                            aria-describedby={errorId + ' ' + infoId}
                            className="input-checkbox"
                            name="stored_customer_email_permission"
                            checked={emailPermission}
                            onChange={(e) =>
                              setEmailPermission(e.target.checked)
                            }
                          />
                        </DirtyWrapper>
                      </InputCheckboxWrapper>
                      <div>
                        <span>
                          {api.trans(
                            'payment_form.stored_customer_email_permission',
                          )}
                        </span>
                        <span className="hint">
                          {api.trans('payment_form.optional')}
                        </span>
                      </div>
                    </label>
                  </RippleWrapper>

                  {!!infoHTML && (
                    <div className="footer">
                      <div
                        className="text"
                        dangerouslySetInnerHTML={{__html: infoHTML}}
                        id={infoId}
                      />
                    </div>
                  )}
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_email_permission"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_6} form={form} />

        {showStoredCustomerMessage &&
          (() => {
            const id = api.ui.generateUniqueElementId('stored_customer_message')
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_message-error',
            )

            return (
              <div className="form-block stored_customer_message">
                <div className="field-block">
                  <label className="label-block" htmlFor={id}>
                    <span>
                      {api.trans('payment_form.stored_customer_message')}
                    </span>
                    <span className="hint">
                      {api.trans('payment_form.optional')}
                    </span>
                  </label>
                  <InputWrapper form={form} fields="stored_customer_message">
                    <DirtyWrapper form={form} fields="stored_customer_message">
                      <TextareaWrapper>
                        <textarea
                          className="input-textarea"
                          id={id}
                          aria-describedby={errorId}
                          value={message}
                          name="stored_customer_message"
                          onChange={(e) => setMessage(e.target.value)}
                          rows={1}
                          placeholder={api.trans(
                            'payment_form.placeholders.stored_customer_message',
                          )}
                        />
                      </TextareaWrapper>
                    </DirtyWrapper>
                  </InputWrapper>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_message"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_7} form={form} />

        {showStoredCustomerDonationReceipt &&
          (() => {
            const errorId = api.ui.generateUniqueElementId(
              'stored_customer_donation_receipt-error',
            )
            const infoId = api.ui.generateUniqueElementId(
              'stored_customer_donation_receipt-info',
            )
            const infoHTML = api.trans(
              'blocks.payment_profile.donation_receipt_info_html',
            )

            return (
              <div className="form-block stored_customer_donation_receipt">
                <div className="field-block">
                  <RippleWrapper>
                    <label className="label-checkbox-block">
                      <InputCheckboxWrapper
                        form={form}
                        fields="stored_customer_donation_receipt"
                      >
                        <DirtyWrapper
                          form={form}
                          fields="stored_customer_donation_receipt"
                        >
                          <input
                            type="checkbox"
                            aria-describedby={errorId}
                            className="input-checkbox"
                            name="stored_customer_donation_receipt"
                            checked={donationReceipt}
                            onChange={(e) =>
                              setDonationReceipt(e.target.checked)
                            }
                          />
                        </DirtyWrapper>
                      </InputCheckboxWrapper>
                      <span>
                        {api.trans(
                          'payment_form.stored_customer_donation_receipt',
                        )}
                      </span>
                    </label>
                  </RippleWrapper>

                  {!!infoHTML && (
                    <Transition
                      in={donationReceipt}
                      mountOnEnter
                      unmountOnExit
                      {...slideTransitionProps}
                    >
                      <div className="footer" aria-live="polite">
                        <div
                          className="text"
                          dangerouslySetInnerHTML={{__html: infoHTML}}
                          id={infoId}
                        />
                      </div>
                    </Transition>
                  )}
                </div>
                <ErrorMessage
                  errors={errors}
                  errorKey="stored_customer_donation_receipt"
                  id={errorId}
                />
              </div>
            )
          })()}

        <DynamicSlot name={DynamicSlotName.PROFILE_FORM_END} form={form} />
      </FormPart>
    </fieldset>
  )
})
